import * as React from 'react';
const { useCallback, useEffect, useState } = React;
import cx from 'classnames';
// import format from 'date-fns/format';
import { format } from 'date-fns-tz';

import { ExtraPostList } from './ExtraPostList';
import { ExtraPostUpload } from './ExtraPostUpload';
import { getErrorMessageFromResponse } from 'src/common/utils/getErrorMessageFromResponse';
import { IExtraPost } from 'src/common/models/extraPost';
import { IPostProject } from 'src/common/models/postProject';
import { IPublisher } from 'src/common/models/publisher';
import { ISocialMention } from 'src/common/models/socialMention';
import { LoadSpinner } from 'src/widgets';

import endpoints from 'src/common/config/endpoints';
import { verifyPostProject } from 'src/utils/hooks';
import styles from './ExtraPostPage.scss';

export interface IExtraPostPageProps {
  aspirexAPIEndpoint: string;
  className?: string;
  clientId: string;
  memberId?: number;
  onContactSupport: () => void;
  project?: IPostProject;
  projectId: string;
  publisher?: IPublisher;
  uceAPIEndpoint: string;

}

const fetchSocialMentions = async (
  aspirexAPIEndpoint: string,
  projectId: string,
  clientId: string,
  memberId: number,
  jwtToken: string
): Promise<ISocialMention[]> => {
  const headers = new Headers({
    'Content-Type': 'application/json',
  });
  if (jwtToken) {
    headers.set('Authorization', `Bearer ${jwtToken}`);
  }

  const resp = await fetch(
    `${aspirexAPIEndpoint}/${endpoints.socialMentionEndpoint}?ax_project_id=${projectId}&source=extra_post&client_id=${clientId}&member_id=${memberId}`,
    {
      method: 'GET',
      headers,
      credentials: 'include',
    },
  );

  const json = await resp.json();

  if (json.status && json.status.code === 200) {
    return json.data;
  } else {
    throw new Error(getErrorMessageFromResponse(json));
  }
};
const fetchMember = async (
  aspirexAPIEndpoint: string,
  memberId: number,
  clientId: string,
  jwtToken: string
): Promise<IPublisher> => {
  const headers = new Headers({
    'Content-Type': 'application/json',
  });
  if (jwtToken) {
    headers.set('Authorization', `Bearer ${jwtToken}`);
  }
  const resp = await fetch(
    `${aspirexAPIEndpoint}/member/${memberId}?client_id=${clientId}`,
    {
      method: 'GET',
      headers,
    },
  );
  const json = await resp.json();

  if (json.status && json.status.code === 200) {
    return json.data;
  } else {
    throw new Error(getErrorMessageFromResponse(json));
  }
};
type TExtraPostPageStep = 'list' | 'upload';

/**
 * @type {React.FunctionComponent}
 */
export const ExtraPostPage: React.FunctionComponent<IExtraPostPageProps> = (props) => {
  const {
    aspirexAPIEndpoint,
    className,
    clientId,
    memberId,
    onContactSupport,
    project,
    projectId,
    uceAPIEndpoint
  } = props;

  const [isFetchingExtraPosts, setIsFetchingExtraPosts] = useState(true);
  const [isFetchingMember, setIsFetchingMember] = useState(true);
  const [error, setError] = useState(false);

  // member is a publisher in this scenario so we can reuse IGStoryUpload
  const [member, setMember] = useState<IPublisher>();

  const [extraPosts, setExtraPosts] = useState<IExtraPost[]>([]);
  const [step, setStep] = useState<TExtraPostPageStep>('list');

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) {
        throw new Error('No projectId given.');
      }

      try {
        // First, get the JWT token by verifying the project
        const queryString = window.location.search;
        const params = new URLSearchParams(queryString);
        const token = params.get('token');
        const jwtToken = await verifyPostProject(projectId, token, aspirexAPIEndpoint);

        const socialMentionsResponse = await fetchSocialMentions(
          aspirexAPIEndpoint,
          projectId,
          clientId,
          memberId,
          jwtToken
        );
        
        const extraPosts = socialMentionsResponse.map((socialMention) => {
          return {
            image_link: socialMention.image_url,
            social_account_name: socialMention.social_account_name,
            social_post_type: socialMention.post_type,
            date_posted: format(new Date(socialMention.datetime_posted), 'dd MMM yyyy @ pp z')
          };
        });
        setExtraPosts(extraPosts);
        setIsFetchingExtraPosts(false);

        const memberResponse = await fetchMember(aspirexAPIEndpoint, memberId, clientId, jwtToken); // Pass JWT token to fetchMember
        setMember(memberResponse);
        setIsFetchingMember(false);
      } catch (err) {
        console.error('Error:', err);
        setIsFetchingExtraPosts(false);
        setIsFetchingMember(false);
        setError(true);
      }
    };

    fetchData();
  }, [projectId, aspirexAPIEndpoint, clientId, memberId]);


  const handleClickNextStep = useCallback(() => {
    setStep('upload');
  }, []);

  const handleClickBackStep = useCallback(() => {
    setStep('list');
  }, []);

  return (
    <div className={cx(styles.ExtraPostPage, className)}>
      {(isFetchingExtraPosts || isFetchingMember) && <LoadSpinner />}

      {!isFetchingExtraPosts && step === 'list' && (
        <ExtraPostList extraPosts={extraPosts} onAddPosts={handleClickNextStep} />
      )}

      {error &&
        <div className={styles.error}>An unexpected error has occurred.
          If this persists, please contact us at help@aspireiq.com</div>
      }

      {member && step === 'upload' && (
        <ExtraPostUpload
          extraPostUploadUrl={`${uceAPIEndpoint}/project/${projectId}/${endpoints.contentReviewEndpoint}/extra_post/upload`}
          onBack={handleClickBackStep}
          onContactSupport={onContactSupport}
          project={project}
          publisher={member}
          uceAPIEndpoint={uceAPIEndpoint}
        />
      )}
    </div>
  );
};
